import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isInTopLayer } from '../../lib/modal-focus.ts';
import { Popover } from './popover.tsx';

/** `id` des Hilfetexts zu einer Basis-`id` (für `aria-describedby` am Feld, wenn die Seite die `id` selbst vergibt). */
export function helpHintId(base: string): string {
  return `${base}-hilfe`;
}

/** Höchstens ein Hilfetext zugleich (wie beim Tooltip). */
let closeCurrent: (() => void) | null = null;

/**
 * Erklärtext hinter einem kleinen „?“ (DESIGN Do's and Don'ts [RUDOLF] 13.10.2026, Ticket 1.215) – ein Baustein für das
 * ganze ERP statt Erklärtexten unter Feldern, Schaltern und Karten-Köpfen. Das „?“ ist ein echter Knopf („Hilfe zu
 * ‹label›“); der Text erscheint bei Maus darüber und bei Tastaturfokus und bleibt offen, solange die Maus auf Knopf oder
 * Text steht; Esc schließt. Tippen (Touch) bzw. Enter öffnet ihn als Popover, am Handy als Blatt von unten. Der Text
 * steht immer im DOM (zu: nur für Hilfsmittel) unter der `id` (Prop oder `helpHintId`), damit das Feld ihn per
 * `aria-describedby` als Beschreibung bekommt. Aussehen wie das „?“ der Einstellungen (`DescriptionHelp`), damit beim
 * Umstellen (D11) nichts springt: Kreis 18 px, Trefferfläche 32 px, bei Touch 44 px.
 */
export function HelpHint({ label, text, id }: { label: string; text: string; id?: string }) {
  const ownId = useId();
  const textId = id ?? helpHintId(ownId);
  const anchor = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [pinned, setPinned] = useState(false);
  const lastPointer = useRef<string>('');
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Schließt das Popover (Esc), setzt es den Fokus zurück auf das „?“: dieser Fokus soll den Text nicht gleich wieder zeigen.
  const skipFocusShow = useRef(false);

  const hide = useCallback(() => setPos(null), []);
  const cancelHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = null;
  };
  // Kurz warten: die Maus darf vom Knopf auf den Text wandern, ohne dass er verschwindet.
  const hideSoon = () => {
    cancelHide();
    hideTimer.current = setTimeout(hide, 150);
  };

  function show() {
    cancelHide();
    const rect = anchor.current?.getBoundingClientRect();
    if (!rect) return;
    if (closeCurrent !== hide) closeCurrent?.();
    closeCurrent = hide;
    const width = Math.min(320, window.innerWidth - 32);
    const x = Math.max(16, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 16));
    setPos({ x, y: rect.bottom + 8 });
  }

  useEffect(
    () => () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (closeCurrent === hide) closeCurrent = null;
    },
    [hide],
  );

  useEffect(() => {
    if (!pos) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isInTopLayer(anchor.current)) {
        // Esc schließt nur die Hilfe, nicht zugleich die Seite/Maske darunter.
        e.preventDefault();
        setPos(null);
      }
    };
    // Nur schließen, wenn das „?“ wirklich gewandert ist: Das Scroll-Ereignis kommt erst im nächsten Bild, ein Scrollen
    // durch den vorigen Tab-Fokus (z. B. auf den letzten Link der Einstellungs-Leiste) schlösse sonst den gerade gezeigten Text.
    const onScroll = () => {
      const rect = anchor.current?.getBoundingClientRect();
      if (rect && Math.abs(rect.bottom + 8 - pos.y) < 1) return;
      setPos(null);
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [pos]);

  const floating = pos !== null && !pinned;

  return (
    <span ref={anchor} data-help-hint="" className="inline-flex flex-none align-middle">
      <Popover
        open={pinned}
        onOpenChange={(next) => {
          if (!next) {
            skipFocusShow.current = true;
            // Der Rück-Fokus kommt synchron beim Schließen; danach gilt Tastaturfokus wieder normal.
            setTimeout(() => {
              skipFocusShow.current = false;
            }, 0);
          }
          setPinned(next);
        }}
        label={`Hilfe zu ${label}`}
        fixed
        className="w-80 max-w-[calc(100vw_-_2*var(--space-4))]"
        trigger={(props) => (
          // Die Zeile bleibt so hoch wie das Label; die Trefferfläche ragt darüber hinaus.
          <span className="relative ml-space-1 block size-icon-md flex-none">
            <button
              type="button"
              aria-expanded={props['aria-expanded']}
              aria-controls={props['aria-controls']}
              aria-label={`Hilfe zu ${label}`}
              aria-describedby={textId}
              onPointerDown={(e) => {
                lastPointer.current = e.pointerType;
              }}
              onPointerEnter={(e) => {
                if (e.pointerType === 'mouse') show();
              }}
              onPointerLeave={(e) => {
                if (e.pointerType === 'mouse') hideSoon();
              }}
              onFocus={(e) => {
                if (skipFocusShow.current) {
                  skipFocusShow.current = false;
                  return;
                }
                if (e.currentTarget.matches(':focus-visible')) show();
              }}
              onBlur={hideSoon}
              onClick={() => {
                // Maus: der Text steht schon (Hover). Tippen und Tastatur (Enter/Leertaste) öffnen ihn als Popover.
                const pointer = lastPointer.current;
                lastPointer.current = '';
                if (pointer === 'mouse') return;
                hide();
                props.onClick();
              }}
              className="absolute top-1/2 left-1/2 grid size-8 -translate-x-1/2 -translate-y-1/2 cursor-pointer place-items-center rounded-full text-ink-2 hover:bg-tile hover:text-ink focus-visible:outline-[length:var(--focus-w)] focus-visible:outline-ring pointer-coarse:size-11"
            >
              <span aria-hidden className="grid size-icon-md place-items-center rounded-full border border-current text-2xs leading-none font-semibold">
                ?
              </span>
            </button>
          </span>
        )}
      >
        <p className="type-meta p-space-1 text-ink-2">{text}</p>
      </Popover>
      {floating ? (
        createPortal(
          <span
            id={textId}
            role="tooltip"
            data-testid="hilfe-text"
            onPointerEnter={cancelHide}
            onPointerLeave={hideSoon}
            className="type-meta fixed z-[var(--z-60)] w-max max-w-tip rounded-tile bg-card px-space-3 py-space-2 text-ink-2 shadow-float"
            style={{ left: pos.x, top: pos.y }}
          >
            {text}
          </span>,
          document.body,
        )
      ) : (
        <span id={textId} className="sr-only">
          {text}
        </span>
      )}
    </span>
  );
}

/**
 * Überschrift einer Seite oder Karte mit Erklärung hinter dem „?“ (Ticket 1.157 Teil B): ein Baustein für alle
 * handgebauten Köpfe (Einstellungs-Seiten, Gruppenköpfe), damit die Erklärung nie als Absatz unter dem Titel steht.
 * Ohne `help` nur die Überschrift.
 */
export function TitleHelp({ title, help, id }: { title: string; help?: string; id?: string }) {
  return (
    <div className="flex min-w-0 items-center" data-title-help="">
      <h2 id={id} className="type-card-title">
        {title}
      </h2>
      {help ? <HelpHint label={title} text={help} /> : null}
    </div>
  );
}
